import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const ELEMENT_POLL_INTERVAL_MS = 250;
const DEFAULT_STEP_WAIT_MS = 9000;
const TOUR_VERSION = '12';

const TOUR_STEPS = [
    // Dashboard
    {
        element: '#tour-total-spend',
        popover: {
            title: 'Total Cloud Spend',
            description: 'This card tracks your aggregate expenditure across all connected Kubernetes clusters. It provides a real-time pulse of your cloud investment.',
            side: 'bottom',
            align: 'start',
        },
        path: '/dashboard',
    },
    {
        element: '#tour-realized-savings',
        popover: {
            title: 'Realized Savings',
            description: 'See the actual dollars saved! This reflects the impact of applied AI recommendations and automated node optimizations.',
            side: 'bottom',
            align: 'start',
        },
        path: '/dashboard'
    },
    {
        element: '#tour-potential-savings',
        popover: {
            title: 'Potential Savings',
            description: 'There is always room for more efficiency! Click "Next Step" to see exactly how we can reduce your bill in the Rightsizing section.',
            side: 'bottom',
            align: 'start',
        },
        path: '/dashboard',
        nextPath: '/rightsizing'
    },
    // Rightsizing
    {
        element: '#tour-monthly-potential',
        popover: {
            title: 'Rightsizing Analysis',
            description: 'Our AI analyzes Every. Single. Pod. We suggest precise CPU and Memory adjustments based on historical usage patterns.',
            side: 'bottom',
            align: 'start',
        },
        path: '/rightsizing'
    },
    {
        element: '#tour-autofix-btn',
        popover: {
            title: 'Auto-Optimization',
            description: 'Tired of manual updates? The "Auto-Fix" button allows you to apply dozens of recommendations across your cluster with a single click.',
            side: 'bottom',
            align: 'start',
        },
        path: '/rightsizing'
    },
    // Clusters
    {
        element: '#tour-add-cluster-btn',
        popover: {
            title: 'Add Your Cluster',
            description: 'Start onboarding by opening the Add Cluster flow.',
            side: 'bottom',
            align: 'start',
        },
        path: '/clusters',
        onNextAction: 'open-add-cluster-dialog',
        maxWaitMs: 20000,
        allowAutoSkip: false
    },
    {
        element: '#tour-cluster-name-input',
        popover: {
            title: 'Cluster Name',
            description: 'Give the cluster a clear name. Continue and we will create it to generate credentials.',
            side: 'bottom',
            align: 'start',
        },
        path: '/clusters',
        maxWaitMs: 45000,
        allowAutoSkip: false
    },
    {
        element: '#tour-submit-cluster-btn',
        popover: {
            title: 'Create Cluster',
            description: 'Click "Create Cluster" in the dialog. This step will auto-continue when Cluster ID and API key are generated.',
            side: 'top',
            align: 'start',
        },
        path: '/clusters',
        maxWaitMs: 45000,
        allowAutoSkip: false,
        waitForExternalCompletion: true
    },
    {
        element: '#tour-cluster-id',
        popover: {
            title: 'Cluster ID & API Key',
            description: 'These credentials are generated after creation and are used by the OptOps agent.',
            side: 'top',
            align: 'start',
        },
        path: '/clusters',
        maxWaitMs: 90000,
        allowAutoSkip: false
    },
    {
        element: '#tour-install-command',
        popover: {
            title: 'Install Command',
            description: 'Run this Helm command in your terminal to connect the cluster using the generated API key and cluster ID.',
            side: 'top',
            align: 'start',
        },
        path: '/clusters',
        maxWaitMs: 90000,
        allowAutoSkip: false
    },
    // Monitoring
    {
        element: '#tour-monitoring-tabs',
        fallbackElements: ['#tour-monitoring-empty-state'],
        popover: {
            title: 'Advanced Analytics',
            description: 'Drill down into specific workloads, namespaces, or view cluster-wide cost trends through these interactive logs.',
            side: 'bottom',
            align: 'start',
        },
        path: '/monitoring'
    },
    // Node Optimization
    {
        element: '#tour-header-cluster-select',
        popover: {
            title: 'Context is Key',
            description: 'Always ensure you have the correct cluster selected before running intensive node-level optimizations.',
            side: 'bottom',
            align: 'start',
        },
        path: '/node-optimization'
    },
    {
        element: '#tour-node-opt-toggle',
        popover: {
            title: 'Optimization Guardrails',
            description: 'Toggle between Recommendation-only mode and Active mode. In active mode, OptOps will automatically execute node scaling actions.',
            side: 'top',
            align: 'start',
        },
        path: '/node-optimization'
    },
    {
        element: '#tour-run-optimizer-btn',
        popover: {
            title: 'The Optimizer Engine',
            description: 'Click this to simulate thousands of Spot vs On-Demand combinations. Our engine builds a plan that maximizes savings while respecting your HA policies.',
            side: 'bottom',
            align: 'start',
        },
        path: '/node-optimization'
    },
    {
        element: '#tour-node-cost-comparison',
        popover: {
            title: 'Visualizing Impact',
            description: 'Analyze the "Before" and "After". We show you exactly how many nodes we can consolidate and what your new monthly bill will look like.',
            side: 'top',
            align: 'start',
        },
        path: '/node-optimization'
    }
];

const GuidedTour = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const driverRef = useRef(null);
    const isTransitioningRef = useRef(false);
    const pollingCleanupRef = useRef(null);
    const [resumeToken, setResumeToken] = useState(0);

    useEffect(() => {
        const savedVersion = localStorage.getItem('optops_tour_version');
        if (savedVersion !== TOUR_VERSION) {
            localStorage.setItem('optops_tour_version', TOUR_VERSION);
            localStorage.removeItem('optops_tour_index');
            localStorage.removeItem('optops_tour_completed');
        }
    }, []);

    const isVisibleElement = useCallback((element) => {
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    }, []);

    const getVisibleSelector = useCallback((step) => {
        const selectors = [step.element, ...(step.fallbackElements || [])];
        for (const selector of selectors) {
            const element = document.querySelector(selector);
            if (isVisibleElement(element)) {
                return selector;
            }
        }
        return null;
    }, [isVisibleElement]);

    const getStepSelector = useCallback((step) => {
        return [step.element, ...(step.fallbackElements || [])].join(', ');
    }, []);

    const scrollStepIntoView = useCallback((step) => {
        const selector = getVisibleSelector(step);
        if (!selector) return false;

        const element = document.querySelector(selector);
        if (!element) return false;

        element.scrollIntoView({
            behavior: 'auto',
            block: 'center',
            inline: 'nearest',
        });

        return true;
    }, [getVisibleSelector]);

    const cleanupDriverArtifacts = useCallback(() => {
        document.body.classList.remove('driver-active', 'driver-fade', 'driver-simple');

        document.querySelectorAll('.driver-active-element, .driver-no-interaction').forEach((element) => {
            element.classList.remove('driver-active-element', 'driver-no-interaction');
            element.removeAttribute('aria-haspopup');
            element.removeAttribute('aria-expanded');
            element.removeAttribute('aria-controls');
        });

        document.querySelectorAll('.driver-popover, .driver-overlay').forEach((node) => {
            node.remove();
        });

        const dummy = document.getElementById('driver-dummy-element');
        if (dummy) {
            dummy.remove();
        }
    }, []);

    const clearActivePolling = useCallback(() => {
        if (pollingCleanupRef.current) {
            pollingCleanupRef.current();
            pollingCleanupRef.current = null;
        }
    }, []);

    const stopTour = useCallback(() => {
        localStorage.setItem('optops_tour_completed', 'true');
        localStorage.removeItem('optops_tour_index');
        isTransitioningRef.current = false;
        clearActivePolling();
        if (driverRef.current) {
            driverRef.current.destroy();
        }
        cleanupDriverArtifacts();
    }, [cleanupDriverArtifacts, clearActivePolling]);

    const runTourAction = useCallback((action) => {
        if (!action) return;

        if (action === 'open-add-cluster-dialog') {
            window.dispatchEvent(new CustomEvent('optops-tour-open-add-cluster-dialog'));
            return;
        }
    }, []);

    const transitionToStep = useCallback((targetIndex, action = null) => {
        if (targetIndex < 0) return;
        clearActivePolling();

        if (targetIndex >= TOUR_STEPS.length) {
            stopTour();
            return;
        }

        const targetStep = TOUR_STEPS[targetIndex];
        const activeDriver = driverRef.current;
        localStorage.setItem('optops_tour_index', targetIndex.toString());

        const canDriveInPlace =
            !action &&
            !!activeDriver &&
            activeDriver.isActive() &&
            targetStep.path === location.pathname &&
            scrollStepIntoView(targetStep);

        if (canDriveInPlace) {
            isTransitioningRef.current = false;
            requestAnimationFrame(() => {
                activeDriver.drive(targetIndex);
            });
            return;
        }

        isTransitioningRef.current = true;

        if (activeDriver) {
            activeDriver.destroy();
        }

        if (action) {
            // Run side-effects (dialog open/submit) before polling the next step element
            runTourAction(action);
        }

        if (targetStep.path !== location.pathname) {
            navigate(targetStep.path);
            return;
        }

        setResumeToken((token) => token + 1);
    }, [clearActivePolling, location.pathname, navigate, runTourAction, scrollStepIntoView, stopTour]);

    const initDriver = useCallback(() => {
        if (driverRef.current) {
            driverRef.current.destroy();
        }
        clearActivePolling();
        cleanupDriverArtifacts();

        const driverObj = driver({
            popoverClass: 'optops-driver-popover',
            showProgress: true,
            animate: false,
            allowClose: true,
            stagePadding: 6,
            popoverOffset: 6,
            overlayColor: 'rgba(15, 23, 42, 0.60)',
            nextBtnText: 'Next Step',
            prevBtnText: 'Back',
            doneBtnText: 'Start Saving',
            onHighlight: (element, step, { config }) => {
                // Keep the index in sync with localStorage
                const currentIndex = driverObj.getActiveIndex();
                if (currentIndex !== undefined) {
                    localStorage.setItem('optops_tour_index', currentIndex.toString());
                }
            },
            steps: TOUR_STEPS.map((step, index) => {
                const stepConfig = {
                    element: getStepSelector(step),
                    popover: {
                        ...step.popover,
                        side: 'bottom',
                        align: 'center',
                    }
                };

                if (step.waitForExternalCompletion) {
                    stepConfig.popover.showButtons = ['previous', 'close'];
                    stepConfig.popover.disableButtons = ['next'];
                }

                // Override Next Click to ensure reliable transitions across conditional UI
                stepConfig.popover.onNextClick = () => {
                    if (step.element === '#tour-submit-cluster-btn') {
                        const createdEl = document.querySelector('#tour-cluster-id');
                        if (!createdEl) {
                            return;
                        }
                    }

                    if (step.autoClickOnNext) {
                        const trigger = document.querySelector(step.element);
                        if (trigger) {
                            trigger.click();
                        }
                    }

                    transitionToStep(index + 1, step.onNextAction);
                };

                stepConfig.popover.onCloseClick = () => {
                    stopTour();
                };

                // Override Prev Click to keep route and index in sync
                if (index > 0) {
                    stepConfig.popover.onPrevClick = () => {
                        transitionToStep(index - 1);
                    };
                }

                return stepConfig;
            }),
            onDestroyed: () => {
                clearActivePolling();
                cleanupDriverArtifacts();
            },
        });

        driverRef.current = driverObj;
        return driverObj;
    }, [cleanupDriverArtifacts, clearActivePolling, getStepSelector, stopTour, transitionToStep]);

    // Robust element polling and drive function
    const driveStep = useCallback((index) => {
        const step = TOUR_STEPS[index];
        if (!step) return;

        // Ensure we are on the right path
        if (step.path !== location.pathname && !isTransitioningRef.current) return;

        clearActivePolling();
        const driverObj = initDriver();
        const maxWaitMs = step.maxWaitMs || DEFAULT_STEP_WAIT_MS;
        const shouldAutoSkip = step.allowAutoSkip !== false;
        let cancelled = false;
        let started = false;
        let checkElement;
        let timeoutId;

        const startIfReady = () => {
            if (cancelled || started) return;
            const selector = getVisibleSelector(step);
            if (!selector) return;

            started = true;
            clearInterval(checkElement);
            clearTimeout(timeoutId);

            if (!driverObj.isActive()) {
                isTransitioningRef.current = false;
                const stabilizationDelay = index === 0 ? 1200 : 300;
                setTimeout(() => {
                    if (!cancelled) {
                        scrollStepIntoView(step);
                        driverObj.drive(index);
                    }
                }, stabilizationDelay);
            }
        };

        checkElement = setInterval(startIfReady, ELEMENT_POLL_INTERVAL_MS);
        timeoutId = setTimeout(() => {
            if (cancelled || started) return;
            if (!shouldAutoSkip) return;
            clearInterval(checkElement);
            transitionToStep(index + 1);
        }, maxWaitMs);

        startIfReady();

        const cleanup = () => {
            cancelled = true;
            clearInterval(checkElement);
            clearTimeout(timeoutId);
        };

        pollingCleanupRef.current = cleanup;
        return cleanup;
    }, [clearActivePolling, getVisibleSelector, initDriver, location.pathname, scrollStepIntoView, transitionToStep]);

    // Handle Path Changes and Auto-start/Resume
    useEffect(() => {
        const isCompleted = localStorage.getItem('optops_tour_completed') === 'true';
        if (isCompleted && !isTransitioningRef.current) return;

        const savedIndex = parseInt(localStorage.getItem('optops_tour_index') || '0', 10);
        const currentStep = TOUR_STEPS[savedIndex];

        if (!currentStep) {
            localStorage.setItem('optops_tour_index', '0');
            return driveStep(0);
        }

        if (currentStep && currentStep.path === location.pathname) {
            return driveStep(savedIndex);
        }
    }, [location.pathname, driveStep, resumeToken]);

    // Handle Manual Restart Event
    useEffect(() => {
        const handleRestart = () => {
            // Close any existing driver before resetting
            if (driverRef.current) {
                driverRef.current.destroy();
            }
            clearActivePolling();
            cleanupDriverArtifacts();

            localStorage.removeItem('optops_tour_completed');
            localStorage.setItem('optops_tour_index', '0');
            isTransitioningRef.current = true;

            if (location.pathname !== '/dashboard') {
                navigate('/dashboard');
            } else {
                // If already on dashboard, just drive step 0 (it will poll)
                setResumeToken((token) => token + 1);
            }
        };

        window.addEventListener('restart-optops-tour', handleRestart);
        return () => window.removeEventListener('restart-optops-tour', handleRestart);
    }, [cleanupDriverArtifacts, clearActivePolling, location.pathname, navigate]);

    useEffect(() => {
        const submitStepIndex = TOUR_STEPS.findIndex((step) => step.element === '#tour-submit-cluster-btn');
        if (submitStepIndex < 0) return;

        const handleClusterCreated = () => {
            const savedIndex = parseInt(localStorage.getItem('optops_tour_index') || '-1', 10);
            if (savedIndex === submitStepIndex) {
                transitionToStep(submitStepIndex + 1);
            }
        };

        window.addEventListener('optops-tour-cluster-created', handleClusterCreated);
        return () => window.removeEventListener('optops-tour-cluster-created', handleClusterCreated);
    }, [transitionToStep]);

    useEffect(() => {
        return () => {
            if (driverRef.current) {
                driverRef.current.destroy();
            }
            clearActivePolling();
            cleanupDriverArtifacts();
        };
    }, [cleanupDriverArtifacts, clearActivePolling]);

    return null;
};

export default GuidedTour;
